"use client";

import Icon from "../atoms/Icon";
import Button from "../atoms/Button";
import TextLink from "../atoms/TextLink";
import { ArrowUpRight, CircleAlert } from "lucide-react";
import { event } from "@/lib/analytics/gtag";
import type { MembershipAlertVariant } from "../types";
import { ALTRU_MEMBERSHIP_URL } from "@/lib/altru/urls";

interface MembershipAlertProps {
  variant: MembershipAlertVariant;
  expirationDate?: string;
}

/**
 * @component MembershipAlert
 * @description Contextual alert banner displayed in the sidebar based on membership state.
 * - **renew** — warns that the membership is expiring soon (`role="status"`).
 * - **expired** — urgent notice that the membership has expired (`role="alert"`).
 * - **start** — CTA encouraging the user to start a new membership.
 * @param variant - Determines the alert's visual style and messaging (renew, expired, or start).
 * @param expirationDate - Human-readable date shown in the renew variant. Defaults to `"May 5th"`.
 * @returns A styled alert banner, or `null` if the variant is unrecognized.
 */
export default function MembershipAlert({
  variant,
  expirationDate = "May 5th",
}: MembershipAlertProps) {
  if (variant === "renew") {
    return (
      <div className="w-full p-4 bg-used-badge" role="status">
        <div className="flex flex-col gap-3">
          <Icon name="calendar" className="w-5 h-5" />
          <div className="flex flex-col gap-2">
            <p className="text-labels-captions-bold text-charcoal">
              Your membership ends on {expirationDate}.
            </p>
            <p className="text-body-base text-near-black">
              Renew your membership soon to continue visiting, reserving, and
              enjoying member perks.
            </p>
          </div>
          <div className="flex justify-end">
            <TextLink
              as="a"
              href={ALTRU_MEMBERSHIP_URL}
              target="_blank"
              rel="noopener noreferrer"
              size="caption"
              onClick={() => event('membership_renew_clicked', { category: 'membership', label: 'renew_now' })}
            >
              Renew Now
              <ArrowUpRight className="w-4 h-4" aria-hidden="true" />
            </TextLink>
          </div>
        </div>
      </div>
    );
  }

  if (variant === "expired") {
    return (
      <div className="w-full p-4 bg-near-black" role="alert">
        <div className="flex flex-col gap-3">
          <CircleAlert className="w-5 h-5 text-white" aria-hidden="true" />
          <div className="flex flex-col gap-2">
            <p className="text-labels-captions-bold text-white">
              Your membership has expired.
            </p>
            <p className="text-body-base text-border-subtle">
              Renew now to restore guest passes, bookings, and member perks.
            </p>
          </div>
          <TextLink
            as="a"
            href={ALTRU_MEMBERSHIP_URL}
            target="_blank"
            rel="noopener noreferrer"
            size="caption"
            className="text-white"
            onClick={() => event('membership_renew_clicked', { category: 'membership', label: 'renew_expired' })}
          >
            Renew Membership
            <ArrowUpRight className="w-4 h-4" aria-hidden="true" />
          </TextLink>
        </div>
      </div>
    );
  }

  if (variant === "start") {
    return (
      <div className="w-full p-6 bg-white border border-border-subtle">
        <div className="flex flex-col gap-4">
          <h2 className="text-title text-charcoal">
            Become a member to enjoy year-round benefits!
          </h2>
          <div className="flex flex-col gap-1">
            <p className="text-body-base text-near-black">
              Free admission
            </p>
            <p className="text-body-base text-near-black">Guest passes</p>
            <p className="text-body-base text-near-black">
              Access to member-exclusive events
            </p>
          </div>
          <Button
            variant="accent"
            size="full"
            onClick={() => {
              event('membership_learn_more_clicked', { category: 'membership', label: 'start' });
              window.location.href = "https://www.groundsforsculpture.org/membership";
            }}
          >
            Learn More
          </Button>
        </div>
      </div>
    );
  }

  return null;
}
